import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  suggestEquitationPlaces,
  classifyDshbClassKind,
  DSHB_CLASS_KINDS,
  isDshbGridKind,
  isDshbPlaceOnlyGridKind,
  isDshbScoredGridKind,
  resolveConfirmedDshbClassKind,
  buildDshbScoredGridColumns,
} from "score-common";
import AppShell from "./AppShell";
import PageBreadcrumbs from "./PageBreadcrumbs";
import SplitClassChip from "./SplitClassChip";
import ClassGridRow from "./ClassGridRow";
import ClassCeremony from "./ClassCeremony";
import SectionBar from "./SectionBar";
import SectionRow from "./SectionRow";
import AddRidersDialog from "./AddRidersDialog";
import { UndrawnGate, UndrawnPanel } from "./UndrawnList";
import { useRidesForClass } from "../hooks/useRidesForClass";
import { useClassSections, POOL_TAB } from "../hooks/useClassSections";
import { useScoreApi } from "../api/scoreApi";
import { endRide, endRideErrorMessage } from "../util/endRide";
import { useSectionLabel } from "../util/sectionLabel";
import {
  NEW_SECTION_ID,
  POOL_ID,
  ROW_END_ID,
  isSectionClass,
  rowDropId,
  sectionTabId,
} from "../util/sectionGestures";
import { useClassGridTest } from "../hooks/useClassGridTest";
import { classNumberLabel } from "../util/classDisplay";
import { sortByCompetitorNumber } from "../util/classGridNav";

// Group test.observations by their `group` label (e.g. "Walk"/"Trot"/
// "Canter") preserving first-seen order, so the header can span each group
// over its sub-columns. #1747 — observations are deliberately NOT sections:
// no coefficient, never summed. Optionally drops any group named in
// `hiddenGroups` — see the #1750 BLOCKER 2 comment on ClassGrid below for
// why that list exists and where it is allowed to come from.
function groupObservations(observations, hiddenGroups) {
  const groups = [];
  const byGroup = new Map();
  for (const obs of observations ?? []) {
    if (hiddenGroups.includes(obs.group)) continue;
    if (!byGroup.has(obs.group)) {
      const g = { group: obs.group, cols: [] };
      byGroup.set(obs.group, g);
      groups.push(g);
    }
    byGroup.get(obs.group).cols.push(obs);
  }
  return groups;
}

// #1750 BLOCKER 2 (round-2 judge review) — DR133.1.a permits a class limited
// to medium walk and working trot, and DR133.1.k requires that class to
// still use the same DSE scoresheet, so "this class is walk/trot only" is a
// property of the CLASS, not the test def, and it cannot become a def field
// (one published sheet = one def, #1440 SS0a). #1749's two DSE defs both
// carry all six observations, canter included, so the def alone never hides
// anything; something has to compute `hiddenGroups` below.
//
// RESOLVED (card #1783, Jaybill 2026-08-30): the signal is a human-set,
// class-level flag authored in scoreadmin-fe (Class.walkTrotOnly,
// score-common's classSchema). Inferring it from the FVD class name was
// ruled out by a prod check (19/19 equitation classes carry no walk/trot
// marker in their name); a scribe-side toggle was rejected as device-local
// and invisible to other consumers.
//
// `hiddenGroups` is the ONE place that signal is allowed to enter this
// component: an array of `test.observations[].group` values to exclude from
// both the header and every row's cells, defaulting to [] so the grid shows
// every group the resolved test carries. Do not add a second entry point
// (no special-casing "Canter" by name anywhere else in this file or in
// ClassGridRow.jsx): the class's own `walkTrotOnly` flag below is the only
// thing that computes a non-default value.
// Module-level so both arrays are the SAME identity on every render. A
// `= []`/`= [...]` default/literal allocates a fresh array each render,
// which changes the identity `observationGroups`' useMemo is keyed on, so
// the memo never hit and every row's `observations` prop was a new array
// too (round-6 judge review).
const NO_HIDDEN_GROUPS = [];
// #2184 — same module-level stable-identity discipline as NO_HIDDEN_GROUPS
// above, for scoredColumns' own useMemo (and every row's).
const EMPTY_SCORED_COLUMNS = [];
// Stable identity for "no rides yet" (useClassSections resets on a new array).
const EMPTY_RIDES = [];
// The only group DR133.1.a's walk/trot-only classes drop; see
// docs/score/2023_tests/usef-dressage-seat-equitation.json's own
// observations for the "Canter" group label this matches verbatim.
const WALK_TROT_ONLY_HIDDEN_GROUPS = ["Canter"];

// The lifted chip sits just below-right of the finger / pointer, so it never
// hides the tab or row it is being dropped on.
const LIFTED_CHIP_MODIFIERS = [
  ({ transform }) => ({ ...transform, x: transform.x + 28, y: transform.y + 40 }),
];

export default function ClassGrid({ hiddenGroups = NO_HIDDEN_GROUPS }) {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("score");
  const { data, settled, refetch } = useRidesForClass(classId);
  const klass = data?.klass;
  const rides = data?.rides ?? EMPTY_RIDES;
  const { test, loading: testLoading } = useClassGridTest(klass, rides);
  // #2678 PATH 1 / #2821 — `!settled` replaces the old `loading` gate below:
  // `loading` (useCachedResource, common-fe) never resets to `true` on a
  // classId change (#2162), so it could read `false` while `klass`/`rides`
  // still held a PREVIOUS classId's settled answer. `settled` resets to
  // `false` on a genuine deps change (unless the new classId already has a
  // warm-cache hit, itself a real synchronous settle), so gating on it here
  // holds the grid behind its loading state until the CURRENT classId's own
  // answer lands instead of briefly rendering the wrong class.
  const ridesUnsettled = !settled;

  // #2183 — resolved fresh off the CLASS's className, never off the reused
  // Test row's own sheetLayout/scoringModel (see dshb-class-kind.js's doc
  // comment: FVD stamps a championship/suitability class with its
  // QUALIFYING class's Test id, so `test` here is the SAME shared row an
  // ordinary qualifying-class ride uses).
  // #2277 — the stored per-class override wins over the className heuristic.
  const rawDshbKind = classifyDshbClassKind({
    className: klass?.className,
    fvdTestId: test?.fvdId,
    dshbKindOverride: klass?.dshbClassKindOverride,
  });
  // #2745 - same shared gate RideList.jsx uses before routing here in the
  // first place: an unconfirmed plain CHAMPIONSHIP (className match, no DSHB
  // signal) must not render the place-only layout either, or a direct/stale
  // link to /grid would still show it (see resolveConfirmedDshbClassKind's
  // own doc comment).
  const dshbKind = resolveConfirmedDshbClassKind({
    kind: rawDshbKind,
    fvdTestId: test?.fvdId,
    test,
    dshbKindOverride: klass?.dshbClassKindOverride,
  });
  // #2184 — the two grid families (score-common's dshb-class-kind.js is the
  // single shared root; see that file's own comment for why these must not
  // be re-derived inline per component): place-only (CHAMPIONSHIP/
  // SUITABILITY, no Total column, no scored cells) vs. scored (MATERIALE/
  // UNDER_SADDLE, DR211.4 movement-totals — real coefficient-weighted marks
  // and a computed Total). isDshbGridClass is their union, used ONLY for
  // "stay on the grid regardless of sheetLayout" below; anywhere that needs
  // to tell the two families apart uses the two narrower flags.
  const isDshbPlaceOnlyGridClass = isDshbPlaceOnlyGridKind(dshbKind);
  const isDshbScoredGridClass = isDshbScoredGridKind(dshbKind);
  const isDshbGridClass = isDshbGridKind(dshbKind);
  // #2265 - USEF DR203.14 (docs/rules/usef/rulebook-dr-dressage-2026.pdf):
  // "Handler-Any person who holds, walks or trots a horse while it is being
  // judged ... for any In-Hand, Group or Championship class". The person
  // column, jump-to box and count line therefore say Handler for a DSHB
  // Championship / USDFBC Championship and stay Rider everywhere else
  // (Suitability, Materiale and Under Saddle are RIDDEN, DR202.12). Keyed off
  // the already-confirmed dshbKind above (resolveConfirmedDshbClassKind), the
  // same gate isDshbHandlerClass applies for ScoringForm, so an unconfirmed
  // ridden "Champ" class cannot print Handler. Static keys only, no dynamic
  // key construction.
  const isHandlerClass =
    dshbKind === DSHB_CLASS_KINDS.CHAMPIONSHIP || dshbKind === DSHB_CLASS_KINDS.USDFBC_CHAMPIONSHIP;

  // #3293 - equitation / medal sections. Gated on the TEST ID (never on
  // sheetLayout or isDshbGridKind, which only keep the DSHB kinds out).
  const sectionMode = isSectionClass(rides) && !isDshbGridClass;
  const {
    groups: sectionGroups,
    current: currentTab,
    selectTab,
    drop: dropRider,
    addRiders,
    error: sectionError,
  } = useClassSections({ classId, rides, enabled: sectionMode });
  const sectionLabel = useSectionLabel();

  // Defensive: a scribe navigating straight to /grid on a movement-grid (or
  // still-unresolved) class belongs on the ordinary ride list, not here.
  // RideList's own redirect (the normal path in) never produces this case.
  // #2183 — a CHAMPIONSHIP/SUITABILITY class stays on the grid regardless of
  // test.sheetLayout (see above): the reused Test's own layout is the
  // qualifying class's, "movement-grid" on the real WEC/Del Mar files.
  // #2184 — MATERIALE/UNDER_SADDLE stay for the analogous reason: their own
  // real Test row's sheetLayout is never authored "class-grid" either.
  useEffect(() => {
    if (!testLoading && test && test.sheetLayout !== "class-grid" && !isDshbGridClass) {
      navigate(`/classes/${classId}`, { replace: true });
    }
  }, [testLoading, test, isDshbGridClass, classId, navigate]);

  // #1783: the computed value #1750 BLOCKER 2 left open: a walk/trot-only
  // class always hides Canter, IN ADDITION to whatever (if anything) a
  // caller passed as `hiddenGroups` — this is a union, never a replacement,
  // so a future caller-supplied value is never silently discarded. useMemo
  // is required here, not optional: a fresh array literal on every render
  // would defeat the module-level identity discipline above and re-trigger
  // `observationGroups`' own memo (and every row's) on every render, the
  // exact bug the round-6 judge comment on `NO_HIDDEN_GROUPS` documents.
  const effectiveHiddenGroups = useMemo(
    () =>
      klass?.walkTrotOnly
        ? [...new Set([...WALK_TROT_ONLY_HIDDEN_GROUPS, ...hiddenGroups])]
        : hiddenGroups,
    [klass?.walkTrotOnly, hiddenGroups],
  );

  const observationGroups = useMemo(
    () => groupObservations(test?.observations, effectiveHiddenGroups),
    [test?.observations, effectiveHiddenGroups],
  );
  const flatObservations = useMemo(
    () => observationGroups.flatMap((g) => g.cols),
    [observationGroups],
  );

  // #2184 — the scored columns for a MATERIALE/UNDER_SADDLE row: Walk/Trot/
  // Canter/General Impression (or whatever the resolved test actually
  // carries — see buildDshbScoredGridColumns's own comment), each a REAL
  // mark with a coefficient, unlike observationGroups above (unscored, no
  // coefficient, DSE/DSM only). Empty for every other class, including
  // place-only — nothing to score there either.
  const scoredColumns = useMemo(
    () => (isDshbScoredGridClass ? buildDshbScoredGridColumns(test) : EMPTY_SCORED_COLUMNS),
    [isDshbScoredGridClass, test],
  );

  // Section mode: the rows are every rider still riding (an ended rider has
  // nothing to score), in section then draw order, undrawn riders last. All of
  // them stay MOUNTED and the non-current ones are only hidden: each row owns
  // its sheet and answers the class-wide ceremony, so unmounting a section
  // would silently drop its riders from Confirm and sign.
  const sortedRides = useMemo(
    () =>
      sectionMode
        ? [...sectionGroups.sections.flatMap((s) => s.rides), ...sectionGroups.pool]
        : sortByCompetitorNumber(rides),
    [sectionMode, sectionGroups, rides],
  );

  // F4 (round-2 judge review) — the missing-Total count used to read
  // Ride.percentage (the reconcile-stamped cache from useRidesForClass), not
  // the live sheet, so it never decremented as the scribe typed and read
  // "N of N still need a Total" forever on a fresh class. Each row reports
  // its own live sheet.percentage state up via onTotalChange; a row that
  // hasn't reported yet (still loading) counts as "still needs a Total" —
  // the same conservative default the old cache-based count effectively had
  // before any row had scored anything.
  const [hasTotalByRide, setHasTotalByRide] = useState({});
  const onTotalChange = useCallback((rideId, hasTotal) => {
    setHasTotalByRide((prev) => (prev[rideId] === hasTotal ? prev : { ...prev, [rideId]: hasTotal }));
  }, []);
  // #3293 - live Total % per rider (null = scored nothing yet), reported by
  // each row once its sheet has loaded; the suggested Place ranks ALL of them,
  // across every section.
  const [percentByRide, setPercentByRide] = useState({});
  const onPercentageChange = useCallback((rideId, pct) => {
    setPercentByRide((prev) => (prev[rideId] === pct ? prev : { ...prev, [rideId]: pct }));
  }, []);
  const suggestedPlaces = useMemo(() => {
    if (!sectionMode) return null;
    return suggestEquitationPlaces(
      sortedRides.map((r) => ({ rideId: r.id, score: percentByRide[r.id] ?? null })),
      klass?.placesAwarded,
    );
  }, [sectionMode, sortedRides, percentByRide, klass?.placesAwarded]);
  const autofillReady = sectionMode && sortedRides.every((r) => r.id in percentByRide);

  // #2183 — DR133.1.j (the rule this gate enforces) does not apply to a
  // place-only class: there is no Total column, no percentage to be
  // missing, so the count (and the banner/confirm-gate it drives) is forced
  // to 0 rather than reading hasTotalByRide, which would otherwise stay
  // permanently "missing" (typeof sheet?.percentage === "number" is
  // permanently false on a placement-only sheet) and block confirm forever.
  // #2184 — a SCORED grid class (MATERIALE/UNDER_SADDLE) DOES have a real
  // Total (DR211.4/DR211.5: a computed percentage against a possible 100),
  // so it is NOT forced to 0 here — it counts the same as an ordinary/
  // observations-grid class, just against a different "hasTotal" signal
  // (ClassGridRow reports whether every scored cell on the row is filled,
  // since a scored sheet's percentage is server-computed only at confirm/
  // complete time, not typed by the scribe).
  const missingTotalCount = isDshbPlaceOnlyGridClass
    ? 0
    : sortedRides.filter((r) => hasTotalByRide[r.id] !== true).length;

  // ── #1751: the class-level ceremony ───────────────────────────────────
  //
  // Each row owns its own ScoreSheet (never lift that to the parent, see
  // ClassGridRow's header), so a class-level action is issued as a COMMAND the
  // rows execute against their own sheets. `ceremonyOp` is that command;
  // `rowStates` is what comes back the other way.
  const [rowStates, setRowStates] = useState({});
  const onSheetStateChange = useCallback((rideId, next) => {
    setRowStates((prev) => {
      const cur = prev[rideId];
      if (
        cur &&
        cur.isConfirmed === next.isConfirmed &&
        cur.isCompleted === next.isCompleted &&
        cur.signedKey === next.signedKey &&
        cur.noSignatureNeeded === next.noSignatureNeeded
      ) {
        return prev;
      }
      return { ...prev, [rideId]: next };
    });
  }, []);

  // Shape the ceremony reads: signedKey (a string, so it can be an effect dep
  // in the row) split back into the ids it encodes.
  const ceremonyRowStates = useMemo(() => {
    const out = {};
    for (const [rideId, st] of Object.entries(rowStates)) {
      out[rideId] = {
        isConfirmed: st.isConfirmed,
        isCompleted: st.isCompleted,
        signedJudgeIds: st.signedKey ? st.signedKey.split(",") : [],
        noSignatureNeeded: st.noSignatureNeeded,
      };
    }
    return out;
  }, [rowStates]);

  const [ceremonyOp, setCeremonyOp] = useState(null);
  const [ceremonyErrorCount, setCeremonyErrorCount] = useState(0);

  // Rows report per ride id, and a row can report twice if it remounts
  // mid-fan-out, so progress is counted over a SET of ride ids rather than a
  // running total — a double report would otherwise end the ceremony early,
  // while rows were still working, and that is precisely the half-finished
  // state this card exists to make impossible to reach by accident.
  //
  // These are refs, not state, for two reasons. onCeremonyResult must be
  // IDENTITY-STABLE (every row holds it in a useEffect dependency list, so a
  // new identity would re-fire the command in every row), and the bookkeeping
  // must not live inside a setState updater — React may invoke an updater
  // twice, which would double-count errors.
  const reportedRef = useRef(new Set());
  const errorCountRef = useRef(0);
  // ceremonyOpRef is the SOURCE OF TRUTH for the in-flight op, and it is
  // deliberately never assigned from render. Mirroring `ceremonyOp` into it on
  // every render looked harmless and was not: runCeremony sets the ref
  // imperatively and then calls setState, so any render that happened in
  // between (a row reporting its own state is enough, and 25 of them do that
  // on every ceremony) wrote the STALE state value back over the fresh op.
  // Rows then reported against an op the ref no longer recognised, their
  // reports were dropped, and the ceremony never ended: the busy dialog stayed
  // up and the next stage's buttons stayed disabled forever. Caught by the
  // AC4 Cypress test, which is the only one that runs two stages in a row.
  const ceremonyOpRef = useRef(null);
  const rideCountRef = useRef(0);
  rideCountRef.current = sortedRides.length;

  const runCeremony = useCallback((kind, judgeId) => {
    reportedRef.current = new Set();
    errorCountRef.current = 0;
    setCeremonyErrorCount(0);
    const op = { opId: `${kind}:${judgeId ?? ""}:${Date.now()}`, kind, judgeId, done: 0 };
    ceremonyOpRef.current = op;
    setCeremonyOp(op);
  }, []);

  const onCeremonyResult = useCallback((rideId, opId, error) => {
    const op = ceremonyOpRef.current;
    if (!op || op.opId !== opId || reportedRef.current.has(rideId)) return;
    reportedRef.current.add(rideId);
    if (error) errorCountRef.current += 1;

    const done = reportedRef.current.size;
    if (done >= rideCountRef.current) {
      ceremonyOpRef.current = null;
      setCeremonyErrorCount(errorCountRef.current);
      setCeremonyOp(null);
    } else {
      const next = { ...op, done };
      ceremonyOpRef.current = next;
      setCeremonyOp(next);
    }
  }, []);

  // ── #3293: sections (drag, keyboard, end-of-class gate) ───────────────
  //
  // Nothing locks per rider. Moves stay open until the WHOLE class is
  // confirmed and signed once (every row completed); after that nothing can be
  // dragged, and score-api refuses a late write with a 409 as well.
  const classSigned =
    sectionMode &&
    sortedRides.length > 0 &&
    sortedRides.every((r) => rowStates[r.id]?.isCompleted === true);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // A press-and-hold lifts a row; a quick swipe is a scroll, not a drag.
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [kbd, setKbd] = useState(null); // { rideId, targetId } keyboard lift
  const [announcement, setAnnouncement] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addTarget, setAddTarget] = useState(null);

  const onDragStart = (e) => {
    setActiveId(String(e.active.id));
    setOverId(null);
  };
  const onDragOver = (e) => setOverId(e.over ? String(e.over.id) : null);
  const clearDrag = () => {
    setActiveId(null);
    setOverId(null);
  };
  const onDragEnd = (e) => {
    const from = String(e.active.id);
    const to = e.over ? String(e.over.id) : null;
    clearDrag();
    if (classSigned || !to) return;
    dropRider(from, to);
  };

  // The keyboard path (the card's "or equal" to dnd-kit's KeyboardSensor,
  // which needs real layout rects to find a target and so cannot be driven or
  // tested without a browser). Focus a row's handle, Space lifts it, Left /
  // Right pick a tab (a section, "+ New section", Not drawn), Up / Down pick
  // the row it will land before, Space drops, Escape cancels. The drop goes
  // through the same dropRider() the pointer path uses.
  const visibleRowIds = useMemo(
    () =>
      sectionMode && typeof currentTab === "number"
        ? (sectionGroups.sections.find((s) => s.number === currentTab)?.rides ?? []).map((r) =>
            rowDropId(r.id),
          )
        : [],
    [sectionMode, currentTab, sectionGroups],
  );
  const tabIds = useMemo(
    () => [...sectionGroups.sections.map((s) => sectionTabId(s.number)), NEW_SECTION_ID, POOL_ID],
    [sectionGroups],
  );
  const onHandleKeyDown = (e, rideId) => {
    if (classSigned) return;
    const key = e.key;
    if (!kbd) {
      if (key === " " || key === "Enter") {
        e.preventDefault();
        setKbd({ rideId, targetId: null });
        setAnnouncement(t("classGrid.sections.announceLifted"));
      }
      return;
    }
    if (kbd.rideId !== rideId) return;
    if (key === "Escape") {
      e.preventDefault();
      setKbd(null);
      setAnnouncement(t("classGrid.sections.announceCancelled"));
      return;
    }
    if (key === " " || key === "Enter") {
      e.preventDefault();
      const { targetId } = kbd;
      setKbd(null);
      if (targetId) dropRider(rideId, targetId);
      setAnnouncement(t("classGrid.sections.announceDropped"));
      return;
    }
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 1, ArrowUp: -1 }[key];
    if (!step) return;
    e.preventDefault();
    const horizontal = key === "ArrowRight" || key === "ArrowLeft";
    const list = horizontal ? tabIds : [...visibleRowIds, ROW_END_ID];
    const at = list.indexOf(kbd.targetId);
    const start = at >= 0 ? at : horizontal ? tabIds.indexOf(sectionTabId(currentTab)) : list.indexOf(rowDropId(rideId));
    const next = list[Math.min(list.length - 1, Math.max(0, (start < 0 ? 0 : start) + step))];
    setKbd({ rideId, targetId: next });
    setAnnouncement(t("classGrid.sections.announceTarget"));
  };
  const highlightId = kbd?.targetId ?? null;
  const dropLineId = activeId ? overId : kbd?.targetId;
  const heldId = activeId ?? kbd?.rideId ?? null;

  // End Ride from the grid (No show / Scratched), for riders never drawn.
  // Direct cloud writes, like everywhere else End Ride is used.
  const scoreApi = useScoreApi();
  const [endBusyId, setEndBusyId] = useState(null);
  const [endError, setEndError] = useState(null);
  const endUndrawn = async (ride, outcome) => {
    setEndBusyId(ride.id);
    setEndError(null);
    try {
      await endRide(scoreApi.rides, ride.id, outcome, null);
      await refetch?.();
    } catch (err) {
      setEndError(endRideErrorMessage(err, t("classGrid.sections.endFailed")));
    } finally {
      setEndBusyId(null);
    }
  };
  const openAdd = (target) => {
    setAddTarget(target);
    setAddOpen(true);
  };

  const rowElRefs = useRef({});
  const firstInputRefs = useRef({});
  const [jumpValue, setJumpValue] = useState("");

  // Round-6 judge review — stable identities (the rows hold these in
  // useCallback deps), and a null means the row unmounted, so DROP the entry
  // rather than leaving a dead node behind for jumpToRider to scroll to. A
  // class whose ride list refreshes mid-session used to accumulate one stale
  // entry per removed ride here, forever.
  const registerRowEl = useCallback((id, el) => {
    if (el) rowElRefs.current[id] = el;
    else delete rowElRefs.current[id];
  }, []);
  const registerFirstInput = useCallback((id, el) => {
    if (el) firstInputRefs.current[id] = el;
    else delete firstInputRefs.current[id];
  }, []);

  const jumpToRider = (e) => {
    if (e.key !== "Enter") return;
    const target = sortedRides.find(
      (r) => (r.competitorNumber ?? "").trim() === jumpValue.trim(),
    );
    if (target) {
      // #3293 - a rider in another section: show that section first.
      if (sectionMode && target.subsection && target.subsection !== currentTab) {
        selectTab(target.subsection);
        setTimeout(() => {
          rowElRefs.current[target.id]?.scrollIntoView({ block: "center" });
          firstInputRefs.current[target.id]?.focus();
        }, 50);
        return;
      }
      rowElRefs.current[target.id]?.scrollIntoView({ block: "center" });
      firstInputRefs.current[target.id]?.focus();
    }
  };

  // KIOSK DOCTRINE (#1751 AC6). Mid-ceremony there is no route out: the busy
  // dialog is modal with no dismiss, and the one navigational affordance on
  // this screen loses its link so it cannot be tabbed to and followed either.
  // A scribe who wanders off between "confirm" and "the third judge signs"
  // leaves the class in exactly the half-finished state this card is about.
  const crumbs = [
    { label: t("classes.title"), to: ceremonyOp ? undefined : "/classes" },
    { label: klass ? classNumberLabel(klass) : "…" },
  ];

  const heldRide = heldId ? sortedRides.find((r) => r.id === heldId) : null;
  const dndWrap = (children) =>
    sectionMode ? (
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        onDragCancel={clearDrag}
        accessibility={{ announcements: undefined, screenReaderInstructions: { draggable: "" } }}
      >
        {children}
        {/* dnd-kit sizes the overlay to the dragged row (the full table
            width); the lifted chip is only the rider, so size it to its text
            or it hides the tab the rider is being dropped on. */}
        <DragOverlay style={{ width: "auto", height: "auto" }} modifiers={LIFTED_CHIP_MODIFIERS}>
          {activeId && heldRide ? (
            <Box
              sx={{ px: 2, py: 1, bgcolor: "background.paper", border: 2, borderColor: "primary.main", borderRadius: 1, boxShadow: 4, fontWeight: 700 }}
              data-testid="section-drag-overlay"
            >
              {heldRide.competitorNumber} {heldRide.riderName}
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>
    ) : (
      children
    );

  return (
    <AppShell sessionSubtitle={klass ? classNumberLabel(klass) : undefined} classId={classId}>
      <PageBreadcrumbs items={crumbs} />
      {dndWrap(<>

      {(ridesUnsettled || testLoading) && (
        <Box sx={{ mt: 2 }}>
          <Typography color="text.secondary">{t("classGrid.loading")}</Typography>
        </Box>
      )}

      {!ridesUnsettled && !testLoading && klass && (
        <Box sx={{ mb: 2 }}>
          {/* #1761 — same "Split/RO" chip the class list/search carry (#883),
              so the grid for a split section is never mistaken for its base
              class's. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="h5" fontWeight={700}>
              {classNumberLabel(klass)}
            </Typography>
            <SplitClassChip klass={klass} testId="class-grid-split-chip" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isDshbScoredGridClass
              ? t("classGrid.ridersSummaryDshb", { count: sortedRides.length })
              : isHandlerClass
                ? t("classGrid.handlersSummary", { count: sortedRides.length })
                : t("classGrid.ridersSummary", { count: sortedRides.length })}
          </Typography>

          <TextField
            autoComplete="off"
            size="small"
            label={isHandlerClass ? t("classGrid.jumpToHandler") : t("classGrid.jumpToRider")}
            sx={{ mt: 1.5, maxWidth: 200 }}
            value={jumpValue}
            onChange={(e) => setJumpValue(e.target.value)}
            onKeyDown={jumpToRider}
            inputProps={{ "data-testid": "class-grid-jump-input" }}
          />

          {missingTotalCount > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="class-grid-missing-total-banner">
              {/* #2184 — a scored grid class (DR211.4/DR211.5) cites its own
                  rule family, never DR133.1.j (a DSE/DSM-only citation).
                  #2235 — DR211.5 (docs/rules/usef/rulebook-dr-dressage-2026.pdf,
                  labelled page DR-74, not DR-73: the "(c) USEF 2026 / DR-73"
                  footer falls between DR211.1 and DR211.2, not before .5)
                  has TWO separate clauses: scores are reported as a total
                  against a possible 100%, and SEPARATELY "all scores (if
                  given) and placings must be published". Do not weld them
                  into "placings published as a percentage" - placings are
                  not percentages. The banner must not claim a score is
                  required for every rider either. */}
              {t(
                isDshbScoredGridClass
                  ? "classGrid.missingTotalBannerDshb"
                  : "classGrid.missingTotalBanner",
                {
                  count: missingTotalCount,
                  total: sortedRides.length,
                },
              )}
            </Alert>
          )}

          {/* #3293 - section tabs. Every tab is a drop target. */}
          {sectionMode && (
            <>
              <SectionBar
                sections={sectionGroups.sections}
                poolCount={sectionGroups.pool.length}
                current={currentTab}
                onSelect={selectTab}
                onNewSection={() => !classSigned && openAdd("new")}
                label={sectionLabel}
                highlightId={highlightId}
                locked={classSigned}
              />
              <Box role="status" aria-live="polite" sx={{ position: "absolute", left: -9999 }} data-testid="section-live">
                {announcement}
              </Box>
              {classSigned && (
                <Alert severity="info" sx={{ mb: 1 }} data-testid="section-locked-note">
                  {t("classGrid.sections.lockedNote")}
                </Alert>
              )}
              {sectionError && (
                <Alert severity="error" sx={{ mb: 1 }} data-testid="section-error">
                  {t("classGrid.sections.moveFailed")}
                </Alert>
              )}
            </>
          )}
        </Box>
      )}

      {sectionMode && !ridesUnsettled && !testLoading && currentTab === POOL_TAB && (
        <UndrawnPanel
          pool={sectionGroups.pool}
          ended={sectionGroups.ended}
          locked={classSigned}
          onEnd={endUndrawn}
          busyId={endBusyId}
          error={endError}
          canAdd={!classSigned}
          onAddToCurrent={() => openAdd("new")}
        />
      )}

      {sectionMode && !ridesUnsettled && !testLoading && typeof currentTab === "number" && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            variant="outlined"
            disabled={classSigned || sectionGroups.pool.length === 0}
            onClick={() => openAdd(currentTab)}
            sx={{ minHeight: 48, textTransform: "none" }}
            data-testid="section-add-riders"
          >
            {t("classGrid.sections.addRiders")}
          </Button>
        </Box>
      )}

      {sectionMode && (
        <AddRidersDialog
          open={addOpen}
          pool={sectionGroups.pool}
          sectionName={addTarget === "new" || addTarget == null ? t("classGrid.sections.newSectionName") : sectionLabel(addTarget)}
          onClose={() => setAddOpen(false)}
          onAdd={(ids) => addRiders(ids, addTarget === "new" || addTarget == null ? "new" : addTarget)}
        />
      )}

      {!ridesUnsettled && !testLoading && sortedRides.length > 0 && (
        <TableContainer
          hidden={sectionMode && currentTab === POOL_TAB}
          sx={{
            maxHeight: "70vh",
            overflow: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Table size="small" stickyHeader data-testid="class-grid-table">
            <TableHead>
              <TableRow>
                <TableCell
                  rowSpan={2}
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                    bgcolor: "background.paper",
                    borderRight: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {sectionMode
                    ? t("classGrid.sections.cnoColumn")
                    : isHandlerClass
                      ? t("classGrid.handlerNoColumn")
                      : t("classGrid.riderNoColumn")}
                </TableCell>
                {sectionMode && (
                  <TableCell rowSpan={2} sx={{ bgcolor: "background.paper" }}>
                    {t("classGrid.sections.riderHorseColumn")}
                  </TableCell>
                )}
                {/* #2184 — a scored grid class (MATERIALE/UNDER_SADDLE) has no
                    observationGroups at all (test.observations stays empty for
                    these defs — they score real movements/collectives
                    instead), so it renders scoredColumns here in exactly the
                    same header slot, one cell per scored column (coefficient
                    goes in the sub-header row below, mirroring how a DSE
                    group's sub-columns work). */}
                {!sectionMode && (isDshbScoredGridClass
                  ? scoredColumns.map((col) => (
                      <TableCell
                        key={`${col.type}-${col.movementNumber}`}
                        align="center"
                        sx={{ bgcolor: "background.paper" }}
                      >
                        {col.label}
                      </TableCell>
                    ))
                  : observationGroups.map((g) => (
                      <TableCell
                        key={g.group}
                        align="center"
                        colSpan={g.cols.length}
                        sx={{ bgcolor: "background.paper" }}
                      >
                        {g.group}
                      </TableCell>
                    )))}
                {!sectionMode && (
                  <TableCell rowSpan={2} sx={{ bgcolor: "background.paper" }}>
                    {t("classGrid.notesColumn")}
                  </TableCell>
                )}
                {/* #2183 — no Total column at all for a place-only class: DR204.2.c/
                    DR210.5 award no scores, so there is nothing to type into and
                    nothing to autosave (not merely hidden — see ClassGridRow).
                    #2184 — a SCORED grid class DOES get a Total column (a real,
                    computed percentage), so it is gated on isDshbPlaceOnlyGridClass
                    now, not the old union. */}
                {!isDshbPlaceOnlyGridClass && (
                  <TableCell
                    rowSpan={2}
                    align="center"
                    sx={{ bgcolor: "background.paper", fontWeight: 700 }}
                  >
                    {sectionMode ? t("classGrid.sections.totalPercentColumn") : t("classGrid.totalColumn")}
                  </TableCell>
                )}
                {/* #2183 — Championship Class Sheet's "Prior Score" column
                    (DR210.5). CHAMPIONSHIP only: Suitability (DR204.2.c) is a
                    first-round class, not a re-judging, and has no such column. */}
                {dshbKind === DSHB_CLASS_KINDS.CHAMPIONSHIP && (
                  <TableCell
                    rowSpan={2}
                    align="center"
                    sx={{ bgcolor: "background.paper", fontWeight: 700 }}
                  >
                    {t("classGrid.priorScoreColumn")}
                  </TableCell>
                )}
                <TableCell
                  rowSpan={2}
                  align="center"
                  sx={{ bgcolor: "background.paper", fontWeight: 700 }}
                >
                  {t("classGrid.placeColumn")}
                </TableCell>
              </TableRow>
              {!sectionMode && (
              <TableRow>
                {/* #2184 — the scored columns' sub-header shows the
                    coefficient (e.g. "×3"), the printed sheet's own
                    "Coefficient x3" cell — AC2's "coefficients visible". */}
                {isDshbScoredGridClass
                  ? scoredColumns.map((col) => (
                      <TableCell
                        key={`${col.type}-${col.movementNumber}-coef`}
                        align="center"
                        sx={{ bgcolor: "background.paper" }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {`×${col.coefficient}`}
                        </Typography>
                      </TableCell>
                    ))
                  : flatObservations.map((obs) => (
                      <TableCell
                        key={obs.key}
                        align="center"
                        sx={{ bgcolor: "background.paper" }}
                      >
                        {obs.label}
                      </TableCell>
                    ))}
              </TableRow>
              )}
            </TableHead>
            <TableBody>
              {sortedRides.map((ride) => {
                const common = {
                  ride,
                  klass,
                  test,
                  dshbKind,
                  observations: flatObservations,
                  scoredColumns,
                  registerRowEl,
                  registerFirstInput,
                  onTotalChange,
                  onSheetStateChange,
                  ceremonyOp,
                  onCeremonyResult,
                };
                if (!sectionMode) return <ClassGridRow key={ride.id} {...common} />;
                return (
                  <SectionRow
                    key={ride.id}
                    {...common}
                    locked={classSigned}
                    hidden={ride.subsection !== currentTab}
                    dropLine={Boolean(
                      dropLineId === rowDropId(ride.id) && heldId && heldId !== ride.id,
                    )}
                    lifted={kbd?.rideId === ride.id}
                    onHandleKeyDown={onHandleKeyDown}
                    suggestedPlace={suggestedPlaces?.get(ride.id) ?? null}
                    autofillReady={autofillReady}
                    onPercentageChange={onPercentageChange}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!ridesUnsettled && !testLoading && sortedRides.length === 0 && klass && (
        <Typography color="text.secondary">{t("classGrid.noRides")}</Typography>
      )}

      {!ridesUnsettled && !testLoading && klass && sortedRides.length > 0 && (
        <>
          {ceremonyErrorCount > 0 && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="ceremony-errors">
              {t("classCeremony.errorTitle")}{" "}
              {t("classCeremony.errorBody", {
                count: ceremonyErrorCount,
                total: sortedRides.length,
              })}
            </Alert>
          )}
          {sectionMode && (
            <UndrawnGate
              pool={sectionGroups.pool}
              onEnd={endUndrawn}
              busyId={endBusyId}
              error={endError}
            />
          )}
          <ClassCeremony
            klass={klass}
            test={test}
            rides={sortedRides}
            blockedCount={sectionMode ? sectionGroups.pool.length : 0}
            missingTotalCount={missingTotalCount}
            isScoredGrid={isDshbScoredGridClass}
            rowStates={ceremonyRowStates}
            busyOp={ceremonyOp}
            onRunCeremony={runCeremony}
          />
        </>
      )}
      </>)}
    </AppShell>
  );
}
